import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";

const ACN_LOGO = "/img/acn-logo.png";
const HERO_IMG = "/img/login-hero.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "student" ? "/households" : "/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-brand-bg">
      {/* Left: imagery only, no overlay text */}
      <div className="hidden lg:flex relative bg-[#1f4a3d] items-center justify-center overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Moments of care, service & compassion"
          className="max-h-screen w-auto object-contain"
        />
      </div>

      {/* Right: branding header + tagline hero + sign in */}
      <div className="relative flex flex-col px-6 sm:px-10 py-8">
        {/* Brand bar */}
        <div className="flex items-start justify-end">
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              <div className="font-heading text-lg sm:text-xl font-bold text-[#3B2A8C] uppercase tracking-wide">Akal College of Nursing</div>
              <div className="text-xs sm:text-sm font-semibold text-brand-muted tracking-[0.18em] uppercase">Baru Sahib · Eternal University</div>
            </div>
            <img src={ACN_LOGO} alt="Akal College of Nursing" className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover bg-white border-2 border-[#3B2A8C]/20 shadow-md" />
          </div>
        </div>

        {/* Tagline heading */}
        <div className="mt-10 sm:mt-14 max-w-xl">
          <h1 className="font-heading font-extrabold leading-[1.05] tracking-tight text-[2.25rem] sm:text-[2.75rem] lg:text-[3.25rem]">
            <span className="text-brand-primary">Reach the Unreached</span><br />
            <span className="text-brand-text">to provide Health Care Service</span>
          </h1>
          <div className="mt-4 inline-block">
            <span className="px-4 py-1.5 rounded-full bg-brand-accent text-white text-sm sm:text-base font-bold uppercase tracking-[0.18em]">— Healing Unreached —</span>
          </div>
        </div>

        {/* Sign in card */}
        <div className="mt-10 sm:mt-12 max-w-md w-full">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-muted font-bold">Sign in</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-1 text-brand-text">Welcome back</h2>
          <p className="text-brand-muted mt-1.5 text-sm">Manager · Faculty · Student</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Email</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 pl-10 pr-3 rounded-md bg-brand-surfaceAlt border-2 border-brand-border focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all font-medium"
                  placeholder="you@nursing.edu"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Password</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-10 pr-12 rounded-md bg-brand-surfaceAlt border-2 border-brand-border focus:bg-white focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all font-medium"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50"
                  data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link to="/forgot-password" className="text-sm text-brand-primary font-bold hover:underline" data-testid="link-forgot">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-bold tracking-wide transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-md shadow-brand-primary/20"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>

          <div className="mt-6 text-sm text-brand-muted">
            New student?{" "}
            <Link to="/register" className="text-brand-primary font-bold hover:underline" data-testid="link-register">
              Create an account
            </Link>
            <div className="text-xs text-brand-muted mt-1">Faculty accounts are provisioned by the Manager.</div>
          </div>
        </div>

        <div className="mt-auto pt-8 text-[11px] uppercase tracking-[0.22em] text-brand-muted font-bold">
          Community Health Nursing Posting
        </div>
      </div>
    </div>
  );
}
